import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, AlertTriangle, TrendingUp, Clock, 
  MapPin, Calendar, Eye, Phone, MessageCircle, Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getDashboardStats, getExpiringBusinesses } from '@/lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, expiringRes] = await Promise.all([
          getDashboardStats(),
          getExpiringBusinesses(7)
        ]);
        setStats(statsRes.data);
        setExpiring(expiringRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { 
      title: 'Aktif İşletme', 
      value: stats?.total_active || 0, 
      icon: Building2, 
      color: 'text-green-500' 
    },
    { 
      title: 'Süresi Dolacak (7 gün)', 
      value: stats?.expiring_soon || 0, 
      icon: AlertTriangle, 
      color: 'text-yellow-500' 
    },
    { 
      title: 'Süresi Dolmuş', 
      value: stats?.expired || 0, 
      icon: Clock, 
      color: 'text-red-500' 
    },
    { 
      title: 'Bu Hafta Yeni', 
      value: stats?.new_this_week || 0, 
      icon: TrendingUp, 
      color: 'text-primary' 
    },
  ];

  const statusCards = [
    { title: 'Taslak', value: stats?.draft || 0, color: 'bg-gray-500' },
    { title: 'Ödeme Bekliyor', value: stats?.pending_payment || 0, color: 'bg-yellow-500' },
    { title: 'Askıya Alınmış', value: stats?.suspended || 0, color: 'bg-red-500' },
  ];

  return (
    <div className="space-y-8 page-enter" data-testid="admin-dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">{stat.title}</p>
                    <p className="text-3xl font-heading font-semibold mt-1">
                      {stat.value}
                    </p>
                  </div>
                  <stat.icon className={`w-10 h-10 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Status breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statusCards.map((status, index) => (
          <motion.div
            key={status.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + index * 0.1 }}
          >
            <Card className="bg-card border-white/10">
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${status.color}`} />
                <div>
                  <p className="text-muted-foreground text-sm">{status.title}</p>
                  <p className="text-xl font-heading font-semibold">{status.value}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Expiring Soon Alert */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Card className="bg-card border-white/10">
            <CardHeader className="border-b border-white/10">
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Süresi Dolmak Üzere
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {expiring.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  Süresi dolmak üzere işletme yok
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {expiring.slice(0, 5).map(biz => (
                    <div key={biz.id} className="p-4 hover:bg-white/5 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{biz.name}</p>
                          <p className="text-muted-foreground text-sm flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {biz.city_name}
                          </p>
                        </div>
                        <Badge 
                          variant={biz.days_remaining <= 3 ? 'destructive' : 'secondary'}
                          className="flex items-center gap-1"
                        >
                          <Calendar className="w-3 h-3" />
                          {biz.days_remaining} gün
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Top Cities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card className="bg-card border-white/10">
            <CardHeader className="border-b border-white/10">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5 text-primary" />
                En Çok İşletme
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!stats?.city_stats || stats.city_stats.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  Henüz veri yok
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {stats.city_stats.map((city, index) => (
                    <div key={city.city} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-medium">
                          {index + 1}
                        </span>
                        <span className="font-medium">{city.city}</span>
                      </div>
                      <Badge variant="secondary">{city.count} işletme</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
